/**
 * @fileoverview AssignTargetPeriodParts: the priority picker and selected-prospects panel of Assign
 * Target.
 * @description Split out of AssignProspectTargetModal to keep that dialog within the repo's
 * file-size limit. Both parts carry the `assign-target-period-*` QA locators.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import type { ProspectListItem } from '@/features/sales/types/prospect.types'
import type { SalesTargetPriority } from '@/features/sales/types/sales-target.types'

const PRIORITIES: SalesTargetPriority[] = ['HIGH', 'MEDIUM', 'LOW']

const PRIORITY_LABEL_KEY: Record<SalesTargetPriority, string> = {
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
}

const PRIORITY_TONE: Record<SalesTargetPriority, string> = {
  HIGH: 'border-danger text-danger bg-danger-bg',
  MEDIUM: 'border-status-warning text-status-warning bg-status-warning-bg',
  LOW: 'border-info text-info bg-info-bg',
}

interface PriorityPickerProps {
  value: SalesTargetPriority
  onChange: (priority: SalesTargetPriority) => void
}

/**
 * @component
 * @param {PriorityPickerProps} props - The selected priority and its change handler.
 * @param {SalesTargetPriority} props.value - The currently selected priority.
 * @param {(priority: SalesTargetPriority) => void} props.onChange - Called with the one clicked.
 */
export function PriorityPicker({ value, onChange }: PriorityPickerProps) {
  const { t } = useTranslation()

  return (
    <div className="col-span-full flex flex-col gap-1.5">
      <span className="text-[12.5px] font-semibold text-cell">
        {t('sales.pipeline.assign.fields.priority')}
      </span>
      <div className="flex flex-wrap gap-2" data-testid="assign-target-period-priority-group">
        {PRIORITIES.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => onChange(option)}
            data-testid={`assign-target-period-priority-${option.toLowerCase()}-button`}
            aria-pressed={value === option}
            className={`cursor-pointer rounded-lg border px-4 py-2 text-[13px] font-semibold ${
              value === option ? PRIORITY_TONE[option] : 'border-input-border text-slate bg-white'
            }`}
          >
            {t(`sales.pipeline.assign.priorities.${PRIORITY_LABEL_KEY[option]}`)}
          </button>
        ))}
      </div>
    </div>
  )
}

/**
 * @component
 * @param {{ prospects: ProspectListItem[] }} props - The prospects being assigned.
 * @param {ProspectListItem[]} props.prospects - The selected backlog rows.
 */
export function SelectedProspectsPanel({ prospects }: { prospects: ProspectListItem[] }) {
  const { t } = useTranslation()

  return (
    <div className="col-span-full flex flex-col gap-2 rounded-lg border border-selection-border bg-selection-bg p-3.5">
      <span
        className="text-[11px] font-bold tracking-[0.7px] text-info"
        data-testid="assign-target-period-selection-count"
      >
        {t('sales.pipeline.selectionLine', { count: prospects.length })}
      </span>
      <div className="flex max-h-32 flex-wrap gap-1.5 overflow-y-auto">
        {prospects.map((prospect) => (
          <span
            key={prospect.prospectId}
            data-testid="assign-target-period-selected-prospect"
            className="rounded-md border border-chip-border bg-white px-2.5 py-1 text-[12.5px] font-medium text-info"
          >
            {prospect.bankName}
          </span>
        ))}
      </div>
    </div>
  )
}
